import { useId, useState, type ReactNode } from 'react';
import type {
  CursorPage, EmployeeRow, TreatmentKind, TreatmentOutcome, TreatmentRow,
} from '@dru/types';
import {
  TREATMENT_KIND_HINT, TREATMENT_KIND_LABEL, TREATMENT_KINDS, TREATMENT_OUTCOME_LABEL, TREATMENT_OUTCOMES,
  TREATMENT_REFERRAL_OUTCOMES, formatBytes,
} from '@dru/types';
import {
  Alert, Button, Card, CardHeader, DataTable, Detail, Dialog, EntityCombobox, Field, IconAction, RowActions,
  StatusBadge, Textarea, TextField, cn, useIdempotencyKey, type Column, type Tone,
} from '@dru/ui';
import { Eye, Unlink } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDate, formatDateTime } from '../../../lib/format';
import { toBase64 } from '../../../lib/files';

/**
 * The treatment register's shared pieces: the record/correct dialog, the
 * record itself with its files, the withdrawal, and the tab on a worker's
 * page that lists theirs.
 *
 * What a treatment is here: a dated note that a worker was seen — casualty,
 * first aid or an OPD visit — saying what they came in with, what was done,
 * and where they went afterwards. It is the register the first-aid room
 * keeps, written down where HR can count it. It is NOT a medical case: it
 * carries no results, is never validated and is never signed. A worker who
 * needs an examination gets one arranged from their record.
 */

export const kindTone = (k: TreatmentKind): Tone =>
  k === 'CASUALTY' ? 'danger' : k === 'FIRST_AID' ? 'warning' : 'info';

export const outcomeTone = (o: TreatmentOutcome): Tone =>
  o === 'RETURNED_TO_WORK' ? 'covered'
  : o === 'SENT_HOME' ? 'pending'
  : o === 'REFERRED' ? 'dueSoon'
  : 'overdue';

/** Matches the server allowlist. */
const ACCEPT = 'application/pdf,image/jpeg,image/png';
/** The adapter's body limit, as on the Documents tab. */
const MAX_BYTES = 1_000_000;

const needsDestination = (o: TreatmentOutcome) => TREATMENT_REFERRAL_OUTCOMES.includes(o);

/** An ISO instant as `<input type="datetime-local">` wants it, in local time. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocal(): string {
  return toLocalInput(new Date().toISOString());
}

type Picked = Pick<EmployeeRow, 'id' | 'fullName' | 'employeeCode' | 'status'>;

/**
 * Recording a treatment, or correcting one.
 *
 * The employee is chosen once: a treatment recorded against the wrong worker
 * is withdrawn and recorded again, so the withdrawal — and its reason — stays
 * on the wrong worker's record rather than vanishing in an edit.
 */
export function TreatmentDialog({ employee, existing, onClose, onDone }: {
  /** Fixed, when opened from a worker's own page. */
  employee?: Picked | null;
  existing?: TreatmentRow | null;
  onClose: () => void;
  onDone: (saved: TreatmentRow) => void;
}) {
  const idem = useIdempotencyKey();
  const [who, setWho] = useState<Picked | null>(employee ?? existing?.employee ?? null);
  const [kind, setKind] = useState<TreatmentKind>(existing?.kind ?? 'FIRST_AID');
  const [occurredAt, setOccurredAt] = useState(existing ? toLocalInput(existing.occurredAt) : nowLocal());
  const [location, setLocation] = useState(existing?.location ?? '');
  const [complaint, setComplaint] = useState(existing?.complaint ?? '');
  const [treatmentGiven, setTreatmentGiven] = useState(existing?.treatmentGiven ?? '');
  const [outcome, setOutcome] = useState<TreatmentOutcome>(existing?.outcome ?? 'RETURNED_TO_WORK');
  const [referredTo, setReferredTo] = useState(existing?.referredTo ?? '');
  // No default: a pre-ticked box filed hurried entries as work injuries.
  const [workRelated, setWorkRelated] = useState<boolean | null>(existing ? existing.workRelated : null);
  const [followUpDate, setFollowUpDate] = useState(existing?.followUpDate ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const future = !!occurredAt && new Date(occurredAt).getTime() > Date.now() + 5 * 60_000;
  const destinationMissing = needsDestination(outcome) && referredTo.trim().length === 0;
  const valid = !!who && !!occurredAt && !future
    && complaint.trim().length >= 3 && treatmentGiven.trim().length >= 3 && !destinationMissing
    && workRelated !== null;

  const submit = async () => {
    if (!who || !valid) return;
    setBusy(true);
    setError(null);
    const body = {
      kind,
      occurredAt: new Date(occurredAt).toISOString(),
      location: location.trim() || (existing ? null : undefined),
      complaint: complaint.trim(),
      treatmentGiven: treatmentGiven.trim(),
      outcome,
      referredTo: needsDestination(outcome) ? referredTo.trim() : (existing ? null : undefined),
      workRelated,
      followUpDate: followUpDate || (existing ? null : undefined),
      notes: notes.trim() || (existing ? null : undefined),
    };
    try {
      const saved = existing
        ? await api.patch<TreatmentRow>(`/treatments/${existing.id}`, body)
        : await api.post<TreatmentRow>('/treatments', { employeeId: who.id, ...body }, idem.key());
      idem.rotate();
      onDone(saved);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const [showNotes, setShowNotes] = useState(!!existing?.notes);
  const complaintShort = complaint.trim().length > 0 && complaint.trim().length < 3;
  const givenShort = treatmentGiven.trim().length > 0 && treatmentGiven.trim().length < 3;
  // What still stops the button, said beside it rather than left for the reader to find.
  const missing = !who ? 'Choose the employee'
    : !occurredAt || future ? 'Set when they were seen'
      : complaint.trim().length < 3 ? 'Describe the complaint'
        : treatmentGiven.trim().length < 3 ? 'Describe the treatment given'
          : destinationMissing ? 'Say where they were sent'
            : workRelated === null ? 'Say whether it was work-related' : null;

  return (
    <Dialog
      onClose={busy ? () => undefined : onClose}
      title={existing ? 'Correct this treatment' : 'Record treatment'}
      wide
      info="What the first-aid room or OHC wrote down: who was seen, for what, what was done, and where they went. A register entry only: not a medical examination, and never a fitness decision."
      footer={
        <>
          {missing && !busy && <span className="mr-auto text-caption text-neutral-500 max-sm:hidden">{missing}</span>}
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy} loading={busy}>
            {existing ? 'Save correction' : 'Record treatment'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* 1 ── Who */}
        <FormStep n={1} title="Who was seen">
          {employee || existing ? (
            <p className="text-body text-neutral-900">
              <span className="font-medium">{who?.fullName}</span>{' '}
              <span className="font-mono text-caption text-neutral-500">{who?.employeeCode}</span>
              {existing && <span className="block text-caption text-neutral-500">Recorded against the wrong employee? Withdraw it and record it again.</span>}
            </p>
          ) : (
            <EntityCombobox<EmployeeRow>
              label="Employee"
              required
              value={who as EmployeeRow | null}
              onChange={(e) => setWho(e)}
              fetchOptions={async (search, cursor) => {
                const page = await api.get<CursorPage<EmployeeRow>>(
                  `/employees?limit=20&q=${encodeURIComponent(search)}` + (cursor ? `&cursor=${cursor}` : ''));
                return { items: page.items, nextCursor: page.nextCursor };
              }}
              getKey={(e) => e.id}
              placeholder="Search for an employee…"
              searchPlaceholder="Name, employee ID, mobile or email"
              emptyMessage="No employee matches that. Check the spelling, or the employee ID."
              renderOption={(e) => (
                <>
                  <span className="block font-medium text-neutral-900">{e.fullName}</span>
                  <span className="block font-mono text-caption font-normal text-neutral-500">
                    {e.employeeCode}{e.status !== 'ACTIVE' && ` · ${e.status.toLowerCase().replace(/_/g, ' ')}`}
                  </span>
                </>
              )}
              renderValue={(e) => `${e.fullName} — ${e.employeeCode}`}
            />
          )}
        </FormStep>

        {/* 2 ── What kind, and when */}
        <FormStep n={2} title="Type and date">
          <ChoiceGroup<TreatmentKind>
            label="Type of treatment"
            value={kind}
            onChange={setKind}
            options={TREATMENT_KINDS.map((k) => ({ value: k, label: TREATMENT_KIND_LABEL[k], hint: TREATMENT_KIND_HINT[k] }))}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Seen at" required type="datetime-local" value={occurredAt} onChange={setOccurredAt}
              hint="When the employee was seen, not when this is typed."
              error={future ? 'A treatment cannot be dated in the future' : null}
            />
            <TextField
              label="Where" value={location} onChange={setLocation}
              placeholder="Paint shop, line 2" hint="Optional. Where on the premises."
            />
          </div>
        </FormStep>

        {/* 3 ── What for, and what was done */}
        <FormStep n={3} title="Complaint and treatment given">
          <Field label="Complaint" required hint="What the employee came in with, in plain words."
                 error={complaintShort ? 'A few more words, please' : null}>
            {(f) => (
              <Textarea {...f} rows={2} maxLength={2000} value={complaint}
                        placeholder="Cut on left palm from sheet edge"
                        onChange={(e) => setComplaint(e.target.value)} />
            )}
          </Field>
          <Field label="Treatment given" required hint="What was done. Findings belong in a medical case, not here."
                 error={givenShort ? 'A few more words, please' : null}>
            {(f) => (
              <Textarea {...f} rows={2} maxLength={4000} value={treatmentGiven}
                        placeholder="Cleaned, dressed, tetanus status checked"
                        onChange={(e) => setTreatmentGiven(e.target.value)} />
            )}
          </Field>
        </FormStep>

        {/* 4 ── Where they went afterwards */}
        <FormStep n={4} title="Outcome and follow-up">
          <ChoiceGroup<TreatmentOutcome>
            label="Outcome"
            value={outcome}
            onChange={setOutcome}
            options={TREATMENT_OUTCOMES.map((o) => ({ value: o, label: TREATMENT_OUTCOME_LABEL[o] }))}
          />
          {/* Only the outcomes the server requires a destination for (REFERRED, HOSPITALISED). */}
          {needsDestination(outcome) && (
            <TextField
              label="Sent to" required value={referredTo} onChange={setReferredTo}
              placeholder="Ruby Hall Clinic" hint="The hospital or clinic."
              error={destinationMissing && referredTo !== '' ? 'Say where the employee was sent' : null}
            />
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Follow-up on" type="date" value={followUpDate} onChange={setFollowUpDate}
              hint="Optional. A day to see them again."
            />
            <fieldset>
              <legend className="mb-1.5 text-small font-medium text-neutral-800">
                Work-related? <span className="text-danger-600" aria-hidden>*</span>
              </legend>
              <div className="flex gap-2" role="radiogroup">
                {([[true, 'Yes, arose out of the work'], [false, 'No']] as const).map(([v, label]) => (
                  <button key={String(v)} type="button" role="radio" aria-checked={workRelated === v}
                          onClick={() => setWorkRelated(v)}
                          className={cn('h-10 rounded-btn border px-3 text-small font-medium transition-colors duration-hover',
                            workRelated === v
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50')}>
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>
          {showNotes ? (
            <Field label="Notes" hint="Context for the next reader: a tetanus booster, a witness, a machine tag.">
              {(f) => (
                <Textarea {...f} rows={2} maxLength={4000} value={notes}
                          onChange={(e) => setNotes(e.target.value)} />
              )}
            </Field>
          ) : (
            <button type="button" onClick={() => setShowNotes(true)}
                    className="text-small font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10">
              + Add a note
            </button>
          )}
        </FormStep>

        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
}

/** A numbered part of the record form: four of them, read top to bottom. */
function FormStep({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="space-y-3">
      <h3 className="flex items-center gap-2 text-small font-semibold text-neutral-900">
        <span aria-hidden className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-50 text-caption font-semibold text-primary-700">
          {n}
        </span>
        {title}
      </h3>
      <div className="space-y-3 sm:pl-7">{children}</div>
    </section>
  );
}

/** A short set of one-tap choices, as radio buttons drawn as tiles. */
function ChoiceGroup<T extends string>({ label, value, onChange, options }: {
  label: string; value: T; onChange: (v: T) => void;
  options: Array<{ value: T; label: string; hint?: string }>;
}) {
  const name = useId();
  return (
    <fieldset>
      <legend className="mb-1.5 text-caption text-neutral-700">
        {label}<span className="ml-0.5 text-danger-600" aria-hidden>*</span>
      </legend>
      <div className={cn('grid gap-2', options.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'sm:grid-cols-3')}>
        {options.map((o) => (
          <label
            key={o.value}
            className={cn(
              'flex cursor-pointer flex-col justify-center rounded-btn border px-3 py-2 transition-colors duration-hover',
              'focus-within:ring-2 focus-within:ring-primary-100 max-md:min-h-11',
              value === o.value
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50',
            )}
          >
            <input
              type="radio" name={name} value={o.value} checked={value === o.value}
              onChange={() => onChange(o.value)} className="sr-only"
            />
            <span className="text-small font-medium">{o.label}</span>
            {o.hint && <span className="text-caption text-neutral-500">{o.hint}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Withdrawing one. Soft, with a reason, like removing a document. */
export function WithdrawTreatmentDialog({ record, onClose, onDone }: {
  record: TreatmentRow; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/treatments/${record.id}`, { reason: reason.trim() });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} title="Withdraw this treatment record?">
      <div className="space-y-3">
        <Alert tone="info">
          It stops appearing on {record.employee.fullName}'s record and in the register. It is not
          destroyed: withdrawing something written about a worker is itself worth remembering, and
          any files attached stay on their Documents tab.
        </Alert>
        <TextField
          label="Why is it being withdrawn?" required value={reason} onChange={setReason}
          placeholder="Recorded against the wrong employee" autoFocus
          hint="At least five characters. Kept with the record of the withdrawal."
        />
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={reason.trim().length < 5 || busy} loading={busy}>
            Withdraw
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * One treatment, in full, with its files.
 *
 * Opened from a row. Files are attached here rather than on the Documents
 * tab so the scan is linked to the treatment it belongs to; it lands on the
 * worker's Documents tab as well, because it is the same document.
 */
export function TreatmentDetailDialog({ id, onClose, onChanged }: {
  id: string; onClose: () => void;
  /** After a correction, an attachment or a withdrawal — the list behind should reload. */
  onChanged: () => void;
}) {
  const { permissions } = useAuth();
  const canWrite = permissions.has('treatment.write');
  const rec = useQuery<TreatmentRow>(() => api.get<TreatmentRow>(`/treatments/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const r = rec.data;

  const open = async (documentId: string) => {
    setOpening(documentId);
    setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/treatments/${id}/attachments/${documentId}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setOpening(null);
    }
  };

  const detach = async (documentId: string) => {
    setError(null);
    try {
      await api.del(`/treatments/${id}/attachments/${documentId}`);
      rec.reload();
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (editing && r) {
    return (
      <TreatmentDialog
        existing={r}
        onClose={() => setEditing(false)}
        onDone={() => { setEditing(false); rec.reload(); onChanged(); }}
      />
    );
  }
  if (withdrawing && r) {
    return (
      <WithdrawTreatmentDialog
        record={r}
        onClose={() => setWithdrawing(false)}
        onDone={() => { setWithdrawing(false); onChanged(); onClose(); }}
      />
    );
  }
  if (attaching && r) {
    return (
      <AttachDialog
        record={r}
        onClose={() => setAttaching(false)}
        onDone={() => { setAttaching(false); rec.reload(); onChanged(); }}
      />
    );
  }

  return (
    <Dialog
      onClose={onClose}
      title={r ? `${TREATMENT_KIND_LABEL[r.kind]} — ${r.employee.fullName}` : 'Treatment'}
      wide
      footer={r && canWrite ? (
        <>
          <Button variant="ghost" onClick={() => setWithdrawing(true)}>Withdraw</Button>
          <Button variant="secondary" onClick={() => setAttaching(true)}>Attach a file</Button>
          <Button onClick={() => setEditing(true)}>Correct</Button>
        </>
      ) : undefined}
    >
      {rec.error && <Alert tone="danger">{rec.error}</Alert>}
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      {r && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={kindTone(r.kind)}>{TREATMENT_KIND_LABEL[r.kind]}</StatusBadge>
            <StatusBadge tone={outcomeTone(r.outcome)}>{TREATMENT_OUTCOME_LABEL[r.outcome]}</StatusBadge>
          </div>

          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Detail label="Employee" value={`${r.employee.fullName} · ${r.employee.employeeCode}`} />
            <Detail label="Seen at" value={formatDateTime(r.occurredAt)} />
            <Detail label="Work-related" value={r.workRelated ? 'Yes, arose out of the work' : 'No'} />
            <Detail label="Where" value={r.location ?? '—'} />
            <Detail label="Follow-up" value={r.followUpDate ? formatDate(r.followUpDate) : '—'} />
            {needsDestination(r.outcome) && <Detail label="Sent to" value={r.referredTo ?? '—'} />}
          </dl>

          <div>
            <p className="text-caption font-semibold uppercase tracking-wider text-neutral-500">Complaint</p>
            <p className="mt-1 whitespace-pre-wrap text-body text-neutral-900">{r.complaint}</p>
          </div>
          <div>
            <p className="text-caption font-semibold uppercase tracking-wider text-neutral-500">Treatment given</p>
            <p className="mt-1 whitespace-pre-wrap text-body text-neutral-900">{r.treatmentGiven}</p>
          </div>
          {r.notes && (
            <div>
              <p className="text-caption font-semibold uppercase tracking-wider text-neutral-500">Notes</p>
              <p className="mt-1 whitespace-pre-wrap text-body text-neutral-800">{r.notes}</p>
            </div>
          )}

          <div>
            <p className="text-caption font-semibold uppercase tracking-wider text-neutral-500">Files</p>
            {r.attachments?.length ? (
              <ul className="mt-1 divide-y divide-neutral-200 rounded-card border border-neutral-200">
                {r.attachments.map((a) => (
                  <li key={a.documentId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-neutral-900">{a.title}</p>
                      <p className="text-caption text-neutral-500">{a.fileName} · {formatBytes(a.sizeBytes)}</p>
                    </div>
                    <RowActions>
                      <IconAction
                        label={opening === a.documentId ? `Opening ${a.title}` : `Open ${a.title}`}
                        icon={Eye}
                        loading={opening === a.documentId}
                        onClick={() => open(a.documentId)}
                      />
                      {canWrite && (
                        <IconAction label={`Detach ${a.title} from this treatment`} icon={Unlink} danger onClick={() => detach(a.documentId)} />
                      )}
                    </RowActions>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-small text-neutral-600">
                No files. {canWrite ? 'A register page, an OPD slip or a photograph can be attached.' : ''}
              </p>
            )}
            <p className="mt-2 text-caption text-neutral-500">
              A file here is also on the worker's Documents tab. Opening one is recorded.
            </p>
          </div>

          <p className="text-caption text-neutral-500">
            A register entry from the first-aid room or OHC. Not a medical examination or a fitness decision.
          </p>
          <p className="text-caption text-neutral-500">
            Recorded {formatDateTime(r.createdAt)}{r.updatedAt !== r.createdAt && ` · corrected ${formatDateTime(r.updatedAt)}`}
          </p>
        </div>
      )}
    </Dialog>
  );
}

function AttachDialog({ record, onClose, onDone }: {
  record: TreatmentRow; onClose: () => void; onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tooBig = !!file && file.size > MAX_BYTES;
  const valid = !!file && !tooBig && title.trim().length >= 2;

  const pick = (f: File | null) => {
    setFile(f);
    setError(null);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 160));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const content = await toBase64(file);
      await api.post(`/treatments/${record.id}/attachments`, {
        title: title.trim(), fileName: file.name, mimeType: file.type, content,
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} title="Attach a file to this treatment">
      <div className="space-y-3">
        <div>
          <label htmlFor="treatment-file" className="text-caption text-neutral-700">
            File<span className="ml-0.5 text-danger-600" aria-hidden>*</span>
          </label>
          <input
            id="treatment-file" type="file" accept={ACCEPT}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-small text-neutral-700
                       file:mr-3 file:rounded-btn file:border-0 file:bg-primary-600
                       file:px-3 file:py-2 file:text-sm file:font-medium file:text-white"
          />
          <p className="mt-1 text-caption text-neutral-500">PDF, JPEG or PNG, up to {formatBytes(MAX_BYTES)}.</p>
        </div>
        {tooBig && (
          <Alert tone="warning">
            That file is {formatBytes(file!.size)}, over the {formatBytes(MAX_BYTES)} limit. A lower-resolution scan will go through.
          </Alert>
        )}
        <TextField
          label="Title" required value={title} onChange={setTitle}
          placeholder="First-aid register, 20 October" hint="What the next reader will see before opening it."
        />
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy} loading={busy}>Attach</Button>
        </div>
      </div>
    </Dialog>
  );
}

/** A follow-up day, said the way someone acts on it. */
function followUpCell(d: string | null | undefined) {
  if (!d) return <span className="text-neutral-500">—</span>;
  const today = new Date().toISOString().slice(0, 10);
  if (d === today) return <StatusBadge tone="pending">Today</StatusBadge>;
  return <span className={cn('whitespace-nowrap', d > today ? 'text-neutral-900' : 'text-neutral-500')}>{formatDate(d)}</span>;
}

/**
 * The columns a list of treatments shows, with or without the worker.
 *
 * The register (with the worker) reads like the book an inspector asks for:
 * who, when, what kind, how it ended, whether the work caused it, and when
 * they are next seen. The complaint itself opens with the row. On a worker's
 * own page the complaint stays, since it is the only thing telling one visit
 * from the next.
 */
export function treatmentColumns(opts: { withEmployee: boolean }) {
  const cols: Array<Column<TreatmentRow>> = [];
  if (opts.withEmployee) {
    cols.push({
      key: 'employee', header: 'Employee', alwaysVisible: true,
      cell: (t) => (
        <div className="min-w-0">
          <p className="font-medium text-neutral-900">{t.employee.fullName}</p>
          <p className="font-mono text-caption text-neutral-500">{t.employee.employeeCode}</p>
        </div>
      ),
    });
  }
  cols.push(
    {
      key: 'seen', header: 'Date', alwaysVisible: !opts.withEmployee,
      cell: (t) => (
        <div>
          {/* One line: a date broken over four lines on a tablet reads as four facts. */}
          <p className="whitespace-nowrap text-neutral-900">{formatDateTime(t.occurredAt)}</p>
          {t.location && <p className="text-caption text-neutral-500">{t.location}</p>}
        </div>
      ),
    },
    {
      key: 'kind', header: 'Type', mobileLabel: false,
      cell: (t) => <StatusBadge tone={kindTone(t.kind)}>{TREATMENT_KIND_LABEL[t.kind]}</StatusBadge>,
    },
  );
  if (!opts.withEmployee) {
    cols.push({
      key: 'complaint', header: 'Complaint', className: 'min-w-[14rem]',
      cell: (t) => <span className="line-clamp-2 text-neutral-800">{t.complaint}</span>,
    });
  }
  cols.push(
    {
      key: 'outcome', header: 'Outcome', mobileLabel: false,
      cell: (t) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusBadge tone={outcomeTone(t.outcome)}>{TREATMENT_OUTCOME_LABEL[t.outcome]}</StatusBadge>
          {t.referredTo && <span className="text-caption text-neutral-500">{t.referredTo}</span>}
        </div>
      ),
    },
    {
      key: 'work', header: 'Work-related',
      // With the complaint gone from the register there is room from a tablet up.
      className: opts.withEmployee ? 'hidden md:table-cell' : 'hidden lg:table-cell', detail: true,
      cell: (t) => (t.workRelated
        ? <StatusBadge tone="warning">Work-related</StatusBadge>
        : <span className="text-neutral-500">No</span>),
    },
    {
      key: 'followUp', header: 'Follow-up',
      className: opts.withEmployee ? 'hidden md:table-cell' : 'hidden lg:table-cell', detail: true,
      cell: (t) => followUpCell(t.followUpDate),
    },
    {
      key: 'files', header: 'Files', align: 'right', className: 'hidden xl:table-cell',
      cell: (t) => (t.attachmentCount ? String(t.attachmentCount) : '—'),
    },
  );
  return cols;
}

/** One worker's treatments, on their record. */
export function TreatmentsTab({ employee }: { employee: Picked }) {
  const { permissions } = useAuth();
  const canWrite = permissions.has('treatment.write');
  const [recording, setRecording] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery<CursorPage<TreatmentRow>>(
    () => api.get<CursorPage<TreatmentRow>>(`/employees/${employee.id}/treatments?limit=100`),
    [employee.id],
  );
  const rows = list.data?.items ?? [];
  const workRelated = rows.filter((r) => r.workRelated).length;

  return (
    <Card>
      <CardHeader
        title="Treatments"
        hint={rows.length
          ? `${list.data?.nextCursor ? `Latest ${rows.length} shown` : `${rows.length} recorded`}${workRelated ? `, ${workRelated} work-related` : ''}. Casualty, first aid and OPD visits on the premises.`
          : 'Casualty, first aid and OPD visits on the premises. Not medical cases.'}
        actions={canWrite && <Button onClick={() => setRecording(true)}>Record treatment</Button>}
      />
      <DataTable<TreatmentRow>
        rows={rows}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        rowKey={(t) => t.id}
        onRowClick={(t) => setOpenId(t.id)}
        empty={{
          title: 'No treatments recorded',
          hint: canWrite
            ? 'A visit to the first-aid post or the OHC is recorded here, with the paperwork attached.'
            : 'Treatments recorded by HR appear here.',
        }}
        columns={treatmentColumns({ withEmployee: false })}
      />
      {recording && (
        <TreatmentDialog
          employee={employee}
          onClose={() => setRecording(false)}
          onDone={() => { setRecording(false); list.reload(); }}
        />
      )}
      {openId && (
        <TreatmentDetailDialog id={openId} onClose={() => setOpenId(null)} onChanged={list.reload} />
      )}
    </Card>
  );
}
